import { useState, useCallback } from "react";
import UserService from "@/services/UserService";

export default function useUsersData() {
  const defaultLimit = 100;
  const limitIncrement = 100;
  const defaultSortColumn = "name";
  const defaultSortDirection = "asc";

  // Data State
  const [data, setData] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(defaultLimit);
  
  // Filter/Sort State
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [searchQuery, setSearchQuery] = useState("");

  /**
   * Load users data using the service
   */
  const loadData = useCallback(async () => {
    if (!loadingMore) setLoading(true);
    setError(null);

    // Use the service instead of direct API calls
    const result = await UserService.getUsers({
      searchQuery,
      limit,
      sortColumn,
      sortDirection
    });
    
    if (result.error) {
      setError(result.error);
      setData([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      // Transform data if needed
      const transformedData = UserService.transformUsers(result.data);
      setData(transformedData);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
      setSearching(result.isSearching);
    }
    
    setLoading(false);
    setLoadingMore(false);
  }, [limit, sortColumn, sortDirection, searchQuery, loadingMore]);

  /**
   * Load more users (pagination)
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      setLimit((prevLimit) => prevLimit + limitIncrement);
      setLoadingMore(true);
    }
  }, [hasMore, loadingMore, limitIncrement]);

  /**
   * Handle search query changes
   */
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setLimit(defaultLimit); // Reset limit when search changes
  }, [defaultLimit]);

  /**
   * Update sorting
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    setLimit(defaultLimit); // Reset limit when sorting changes
    setLoading(true);
  }, [defaultLimit]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    data,
    totalCount,
    hasMore,
    
    // UI States
    loading,
    loadingMore,
    searching,
    error,
    
    // Filter/Sort State
    searchQuery,
    sortColumn,
    sortDirection,
    limit,
    
    // Actions
    loadData,
    handleLoadMore,
    handleSearch,
    setSorting,
    
    // State management
    setError: clearError,
  };
}
